import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { useUnsavedGuard } from '../contexts/UnsavedGuardContext';
import { LogoBicolor } from './LogoBicolor';
import {
  LayoutDashboard, Users, FileText, Settings, LogOut, Moon, Sun,
  CreditCard, PlusCircle, Mic, MoreHorizontal, X
} from 'lucide-react';
import { Button } from '../components/ui/button';

export const DashboardLayout = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { toggleTheme, isDark } = useTheme();
  const { requestExit } = useUnsavedGuard();
  const [moreOpen, setMoreOpen] = useState(false);

  const handleLogout = () => {
    requestExit(async () => {
      await logout();
      navigate('/login', { replace: true });
    });
  };

  // Primary items (show in mobile bottom nav + desktop sidebar)
  const primaryNav = [
    { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard, testId: 'sidebar-dashboard' },
    { name: 'Pacientes', href: '/patients', icon: Users, testId: 'sidebar-patients' },
    { name: 'Consultas', href: '/consultations', icon: FileText, testId: 'sidebar-consultations' },
    { name: 'Transcrição', href: '/transcription', icon: Mic, testId: 'sidebar-transcription' }
  ];

  // Secondary items (desktop sidebar shows all; mobile hides behind "Mais")
  const secondaryNav = [
    { name: 'Planos', href: '/billing', icon: CreditCard, testId: 'sidebar-billing' },
    { name: 'Configurações', href: '/settings', icon: Settings, testId: 'sidebar-settings' }
  ];

  const allNav = [...primaryNav, ...secondaryNav];

  const backgroundImage = isDark
    ? 'https://customer-assets.emergentagent.com/job_physio-scribe/artifacts/s3lxzlrk_Gemini_Generated_Image_jhydssjhydssjhyd.png'
    : 'https://customer-assets.emergentagent.com/job_physio-scribe/artifacts/8vc22kdd_Gemini_Generated_Image_5_Fundo%20Claro.png';

  return (
    <div className={`min-h-screen relative ${isDark ? 'bg-[#0F1117]' : 'bg-white'}`}>
      {/* Background Image */}
      <div className="fixed inset-0 z-0 pointer-events-none">
        <img
          src={backgroundImage}
          alt="Background"
          className="w-full h-full object-cover"
          style={{ opacity: isDark ? 0.15 : 0.08 }}
        />
      </div>

      {/* Mobile Top Header (only on < md) */}
      <header
        className={`md:hidden sticky top-0 z-30 border-b backdrop-blur-md ${
          isDark ? 'bg-[#13151F]/90 border-[#2A2E42]' : 'bg-white/90 border-slate-200'
        }`}
        data-testid="mobile-header"
      >
        <div className="flex items-center justify-between px-4 h-14">
          <LogoBicolor size={16} symbolSize={24} />
          <div className="flex items-center gap-1">
            {/* Theme toggle temporarily hidden — design system is dark-mode only. */}
            {false && (
              <Button
                onClick={toggleTheme}
                variant="ghost"
                size="icon"
                className={`h-10 w-10 ${isDark ? 'text-[#C8CEDE] hover:bg-[#22263A]' : 'text-slate-600'}`}
                data-testid="mobile-theme-toggle"
                aria-label="Alternar tema"
              >
                {isDark ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
              </Button>
            )}
            <Button
              onClick={handleLogout}
              variant="ghost"
              size="icon"
              className={`h-10 w-10 ${isDark ? 'text-[#C8CEDE] hover:bg-[#22263A]' : 'text-slate-600'}`}
              data-testid="mobile-logout-btn"
              aria-label="Sair"
            >
              <LogOut className="h-5 w-5" />
            </Button>
          </div>
        </div>
      </header>

      {/* Sidebar Desktop */}
      <div className="hidden md:fixed md:inset-y-0 md:flex md:w-64 md:flex-col z-20">
        <div className={`flex flex-col flex-grow ${isDark ? 'bg-[#13151F]/95 border-[#2A2E42]' : 'bg-white/95 border-slate-200'} border-r backdrop-blur-sm`}>
          <div className={`flex items-center justify-between px-6 py-6 border-b ${isDark ? 'border-[color:var(--color-border)]' : 'border-slate-200'}`}>
            <LogoBicolor size={18} symbolSize={28} />
            {/* Theme toggle temporarily hidden — design system is dark-mode only.
                Keeping the toggle code intact for future light-mode work. */}
            {false && (
              <Button
                onClick={toggleTheme}
                variant="ghost"
                size="icon"
                className={isDark ? 'text-[#C8CEDE] hover:text-white hover:bg-[#22263A]' : 'text-slate-600 hover:text-slate-900'}
                data-testid="theme-toggle-btn"
              >
                {isDark ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
              </Button>
            )}
          </div>

          <nav className="flex-1 px-4 py-6 space-y-2">
            <Link
              to="/consultations/new"
              className="flex items-center gap-3 px-4 py-3 rounded-lg transition-colors mb-4 bg-primary text-white hover:bg-primary-hover"
              data-testid="sidebar-new-consultation"
            >
              <PlusCircle className="h-5 w-5" />
              Nova Consulta
            </Link>

            {allNav.map((item) => {
              const isActive = location.pathname === item.href;
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                    isActive
                      ? isDark
                        ? 'bg-primary/15 text-primary border-l-[3px] border-primary font-medium'
                        : 'bg-primary/10 text-primary font-medium'
                      : isDark
                        ? 'text-[#C8CEDE] hover:bg-[#22263A] hover:text-white'
                        : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
                  }`}
                  data-testid={item.testId}
                >
                  <item.icon className="h-5 w-5" />
                  {item.name}
                </Link>
              );
            })}
          </nav>

          <div className={`px-4 py-6 border-t ${isDark ? 'border-[#2A2E42]' : 'border-slate-200'}`}>
            <div className="flex items-center gap-3 px-4 mb-3">
              <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center text-primary font-semibold">
                {user?.full_name?.charAt(0) || 'U'}
              </div>
              <div className="flex-1 min-w-0">
                <p className={`text-sm font-medium truncate ${isDark ? 'text-[#F0F2FF]' : 'text-slate-900'}`}>{user?.full_name}</p>
                <p className={`text-xs truncate ${isDark ? 'text-[#9BA3BF]' : 'text-slate-500'}`}>{user?.email}</p>
              </div>
            </div>
            <Button
              onClick={handleLogout}
              variant="ghost"
              className={`w-full justify-start ${isDark ? 'text-[#C8CEDE] hover:text-white hover:bg-[#22263A]' : 'text-slate-600 hover:text-slate-900'}`}
              data-testid="logout-btn"
            >
              <LogOut className="h-5 w-5 mr-3" />
              Sair
            </Button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="md:pl-64 relative z-10">
        <main className="py-6 px-4 sm:px-6 lg:px-8 pb-28 md:pb-8">
          {children}
        </main>
      </div>

      {/* Mobile Floating "New Consultation" button */}
      <Link
        to="/consultations/new"
        className="md:hidden fixed bottom-[84px] right-4 z-40 flex items-center gap-2 px-4 py-3 rounded-full shadow-lg bg-primary text-white hover:bg-primary-hover"
        data-testid="mobile-new-consultation-fab"
        aria-label="Nova consulta"
      >
        <PlusCircle className="h-5 w-5" />
        <span className="text-sm font-semibold">Nova</span>
      </Link>

      {/* Mobile Bottom Navigation (5 items max; touch targets >= 48px) */}
      <nav
        className={`md:hidden fixed bottom-0 left-0 right-0 z-30 border-t backdrop-blur-md ${
          isDark ? 'bg-[#13151F]/95 border-[#2A2E42]' : 'bg-white/95 border-slate-200'
        }`}
        aria-label="Navegação principal"
        data-testid="mobile-bottom-nav"
      >
        <ul className="grid grid-cols-5">
          {primaryNav.map((item) => {
            const isActive = location.pathname === item.href;
            return (
              <li key={item.name}>
                <Link
                  to={item.href}
                  className={`flex flex-col items-center justify-center gap-0.5 py-2 min-h-[56px] text-[10px] font-medium transition-colors ${
                    isActive
                      ? 'text-primary'
                      : isDark ? 'text-[#C8CEDE]' : 'text-slate-600'
                  }`}
                  data-testid={`mobile-${item.testId}`}
                >
                  <item.icon className="h-5 w-5" />
                  <span className="truncate px-1">{item.name}</span>
                </Link>
              </li>
            );
          })}
          <li>
            <button
              type="button"
              onClick={() => setMoreOpen(true)}
              className={`w-full flex flex-col items-center justify-center gap-0.5 py-2 min-h-[56px] text-[10px] font-medium ${
                isDark ? 'text-[#C8CEDE]' : 'text-slate-600'
              }`}
              data-testid="mobile-more-btn"
              aria-label="Mais opções"
            >
              <MoreHorizontal className="h-5 w-5" />
              <span>Mais</span>
            </button>
          </li>
        </ul>
      </nav>

      {/* Mobile "Mais" Drawer */}
      {moreOpen && (
        <div className="md:hidden fixed inset-0 z-50" data-testid="mobile-more-drawer">
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => setMoreOpen(false)}
            aria-hidden="true"
          />
          <div
            className={`absolute bottom-0 left-0 right-0 rounded-t-2xl pt-3 pb-6 px-4 animate-in slide-in-from-bottom-5 ${
              isDark ? 'bg-[#13151F] text-[#F0F2FF]' : 'bg-white text-slate-900'
            }`}
          >
            <div className="flex justify-between items-center mb-4 px-2">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center text-primary font-semibold">
                  {user?.full_name?.charAt(0) || 'U'}
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-semibold truncate">{user?.full_name}</p>
                  <p className="text-xs text-slate-500 truncate">{user?.email}</p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setMoreOpen(false)}
                className="h-10 w-10 flex items-center justify-center rounded-full hover:bg-slate-100 dark:hover:bg-[#22263A]"
                data-testid="mobile-more-close"
                aria-label="Fechar"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="grid grid-cols-1 gap-1">
              {secondaryNav.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  onClick={() => setMoreOpen(false)}
                  className={`flex items-center gap-3 px-4 py-3 rounded-lg min-h-[48px] ${
                    isDark ? 'hover:bg-[#22263A]' : 'hover:bg-slate-100'
                  }`}
                  data-testid={`mobile-${item.testId}`}
                >
                  <item.icon className="h-5 w-5 text-primary" />
                  <span className="text-sm font-medium">{item.name}</span>
                </Link>
              ))}
              <button
                type="button"
                onClick={() => { setMoreOpen(false); handleLogout(); }}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg min-h-[48px] text-left ${
                  isDark ? 'hover:bg-[#22263A]' : 'hover:bg-slate-100'
                }`}
                data-testid="mobile-drawer-logout"
              >
                <LogOut className="h-5 w-5 text-red-500" />
                <span className="text-sm font-medium">Sair</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
